import { readonly, ref, type Ref } from 'vue'

export type ToastType = 'success' | 'error' | 'info'

export interface Toast {
  id: number
  type: ToastType
  message: string
}

const DEFAULT_DURATION_MS = 4000
const ERROR_DURATION_MS = 6000

const toasts = ref<Toast[]>([])
const timers = new Map<number, ReturnType<typeof setTimeout>>()
let nextId = 1

function dismiss(id: number): void {
  const timer = timers.get(id)
  if (timer) {
    clearTimeout(timer)
    timers.delete(id)
  }
  toasts.value = toasts.value.filter((toast) => toast.id !== id)
}

function show(type: ToastType, message: string, durationMs?: number): number {
  const id = nextId++
  toasts.value = [...toasts.value, { id, type, message }]

  const duration = durationMs ?? (type === 'error' ? ERROR_DURATION_MS : DEFAULT_DURATION_MS)
  if (duration > 0) {
    timers.set(id, setTimeout(() => dismiss(id), duration))
  }

  return id
}

export function useToast(): {
  toasts: Readonly<Ref<readonly Toast[]>>
  show: (type: ToastType, message: string, durationMs?: number) => number
  success: (message: string) => number
  error: (message: string) => number
  info: (message: string) => number
  dismiss: (id: number) => void
} {
  return {
    toasts: readonly(toasts),
    show,
    success: (message) => show('success', message),
    error: (message) => show('error', message),
    info: (message) => show('info', message),
    dismiss,
  }
}
